/**
 * PermissionDeleteConfirm — 권한 삭제 확인 다이얼로그.
 *
 * Flow:
 * 1. First attempt → DELETE /permissions/{id}  (force=false)
 * 2. On 409 PERM-409-001 (mapped to roles) → show errorMessage + [강제 삭제] button
 * 3. User clicks [강제 삭제] → onForceDelete() → DELETE /permissions/{id}?force=true
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';

interface PermissionDeleteConfirmProps {
  open: boolean;
  permissionCode: string;
  onClose: () => void;
  onConfirm: () => void;
  onForceDelete: () => void;
  isLoading?: boolean;
  errorMessage?: string;
  errorCode?: string;
}

export function PermissionDeleteConfirm({
  open,
  permissionCode,
  onClose,
  onConfirm,
  onForceDelete,
  isLoading = false,
  errorMessage,
  errorCode,
}: PermissionDeleteConfirmProps) {
  const { t } = useTranslation();

  const isMappedToRoles = errorCode === 'PERM-409-001';

  // Esc 키 닫기
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  return (
    <FormDialog
      open={open}
      title={t('permission.delete.title')}
      onClose={onClose}
      onSubmit={onConfirm}
      submitLabel={
        isLoading
          ? t('permission.delete.deleting')
          : t('permission.delete.confirm')
      }
      cancelLabel={t('permission.delete.cancel')}
    >
      <div>
        {/* 기본 확인 메시지 */}
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-primary)',
            marginBottom: 'var(--spacing-3)',
          }}
        >
          {t('permission.delete.description', { code: permissionCode })}
        </p>

        {/* 409 — 역할에 매핑된 권한 */}
        {errorMessage && isMappedToRoles && (
          <div
            role="alert"
            style={{
              padding: 'var(--spacing-3)',
              background:
                'color-mix(in srgb, var(--color-warning) 12%, var(--color-surface-muted))',
              border: '1px solid var(--color-warning)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-warning)',
              marginBottom: 'var(--spacing-3)',
            }}
          >
            <p style={{ marginBottom: 'var(--spacing-2)' }}>
              {t('permission.error.PERM-409-001')}
            </p>
            <button
              type="button"
              onClick={onForceDelete}
              disabled={isLoading}
              style={{
                background: 'var(--color-danger)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                padding: 'var(--spacing-2) var(--spacing-3)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.6 : 1,
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {isLoading
                ? t('permission.delete.deleting')
                : t('permission.delete.forceConfirm')}
            </button>
          </div>
        )}

        {/* 일반 에러 (non-409) */}
        {errorMessage && !isMappedToRoles && (
          <div
            role="alert"
            style={{
              padding: 'var(--spacing-3)',
              background:
                'color-mix(in srgb, var(--color-danger) 10%, var(--color-surface-muted))',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
            }}
          >
            {errorMessage}
          </div>
        )}
      </div>
    </FormDialog>
  );
}
